import { Accordion, Box, Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import {
  type ChangelogHistory,
  fetchChangelogForViewing,
  fetchChangelogHistory,
} from '~/hooks/useElectronIPC'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

interface ChangelogModalProps {
  isOpen?: boolean
  onClose?: () => void
}

const ChangelogModal = ({ isOpen = false, onClose }: ChangelogModalProps) => {
  const { t } = useTranslation()
  const [history, setHistory] = useState<ChangelogHistory | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && isElectron && !history) {
      const loadChangelog = async () => {
        setLoading(true)

        try {
          const historyData = await fetchChangelogHistory()

          if (historyData && historyData.releases.length > 0) {
            setHistory(historyData)
          } else {
            // Fallback to current version if history is missing
            const currentChangelog = await fetchChangelogForViewing()
            if (currentChangelog) {
              // Create a single-item history with current version
              const fallbackHistory: ChangelogHistory = {
                releases: [currentChangelog],
              }
              setHistory(fallbackHistory)
            }
          }
        } catch (error) {
          console.error('Changelog fetch error:', error)
        } finally {
          setLoading(false)
        }
      }

      void loadChangelog()
    }
  }, [isOpen, isElectron, history])

  const handleClose = () => {
    setHistory(null)
    onClose?.()
  }

  if (!isOpen) return null

  return (
    <MangoModal
      isOpen={isOpen}
      header={t('changelogModal.whatsNewLabel')}
      onClose={handleClose}
      size={Size.md}
      body={
        loading ? (
          <Text>{t('changelogModal.loadingData')}</Text>
        ) : history && history.releases.length > 0 ? (
          <Box maxHeight="400px" overflowY="auto">
            <Accordion.Root
              defaultValue={[history.releases[0].version]}
              multiple
            >
              {history.releases.map((release, index) => (
                <Accordion.Item key={release.version} value={release.version}>
                  <Accordion.ItemTrigger>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold">
                        {release.version}{' '}
                        {index === 0 && t('changelogModal.latest', '(Latest)')}
                      </Text>
                      <Text fontSize="sm" color="fg.muted">
                        {t('changelogModal.releasedLabel')}{' '}
                        {new Date(release.published_at).toLocaleDateString()}
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent pb={4}>
                    <Box
                      css={{
                        '& h2': {
                          fontSize: '1.2em',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: 'var(--chakra-colors-on-surface)',
                        },
                        '& h3': {
                          fontSize: '1.1em',
                          fontWeight: 'semibold',
                          marginBottom: '6px',
                          color: 'var(--chakra-colors-on-surface)',
                        },
                        '& ul': {
                          paddingLeft: '20px',
                          marginBottom: '12px',
                          listStyleType: 'disc',
                        },
                        '& li': {
                          marginBottom: '4px',
                          color: 'var(--chakra-colors-on-surface)',
                        },
                        '& p': {
                          marginBottom: '8px',
                          color: 'var(--chakra-colors-on-surface)',
                        },
                        '& strong': {
                          fontWeight: 'bold',
                        },
                        '& em': {
                          fontStyle: 'italic',
                        },
                      }}
                    >
                      <ReactMarkdown>{release.body}</ReactMarkdown>
                    </Box>
                  </Accordion.ItemContent>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </Box>
        ) : (
          <Text>{t('changelogModal.noResults')}</Text>
        )
      }
      footer={
        <Flex justifyContent="flex-end">
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={handleClose}
          >
            {t('changelogModal.doneButton')}
          </MangoButton>
        </Flex>
      }
    />
  )
}

export default ChangelogModal
